import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { IonicModule, LoadingController, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EditTagComponent } from 'src/app/components/edit-tag/edit-tag.component';
import { EditTicketTypeComponent } from 'src/app/components/edit-ticket-type/edit-ticket-type.component';
import { INBOX_TYPES, routes } from 'src/app/constants';
import { Tag } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { TagsService } from 'src/services/tags.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, SharedPipesModule],
  selector: 'app-tags-list',
  templateUrl: './tags-list.component.html',
  styleUrls: ['./tags-list.component.scss'],
})
export class TagsListComponent implements OnInit, OnDestroy {
  tags: Partial<Tag>[] = [];
  filteredTags: Partial<Tag>[] = [];

  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  routerSub: Subscription;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navController: NavController,
    private loadingCtrl: LoadingController,
    private tagsService: TagsService,
    private msgSrvc: MessageService,
    private router: Router,
    private location: Location,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit() {
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        this.isLoading = true;

        await Promise.all([this.getTags()]);

        this.isLoading = false;
      });

    this.route.queryParams.subscribe(async (params) => {
      if (params.id) {
        const modal = await this.modalCtrl.create({
          component: EditTagComponent,
          componentProps: {
            id: params.id,
          },
        });

        modal.present();

        modal.onDidDismiss().then(() => {
          this.getTags();
          this.navController.navigateForward(this.router.url, {
            queryParams: {},
          });
        });
      }
    });
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getTags() {
    try {
      const tagsResponse = await this.tagsService.list({
        limit: null,
        order: ['name'],
        descending: false,
        where: this.authService.selectedProject
          ? [
              {
                field: 'projectId',
                operator: '==',
                value: this.authService.selectedProject.id,
              },
            ]
          : [],
      });

      this.tags = tagsResponse.data;
      this.filteredTags = this.tags;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  showNextPage() {
    this.pageCount += 1;
  }

  handleFilters() {
    this.filteredTags = this.tags;

    const term = this.searchText?.trim().toLowerCase();

    if (term) {
      this.filteredTags = this.tags.filter((tag) => tag.name.toLowerCase().includes(term));
    }
  }

  async toggleAutoTagging(tag: Tag) {
    const loader = await this.loadingCtrl.create({
      message: 'Updating...',
      spinner: 'crescent',
    });

    loader.present();

    await this.tagsService.updatePartial(tag.id, {
      autoTaggingEnabled: !tag.autoTaggingEnabled,
    });

    loader.dismiss();
  }

  viewConversationsForTag(tag: Tag) {
    const filter = [
      [{ label: 'Tag', id: 'tags.id', icon: 'pricetag', type: 'select' }, [{ id: tag.id, label: tag.name }]],
    ];
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.ALL}`, {
      queryParams: {
        filters: JSON.stringify(filter),
      },
      queryParamsHandling: 'merge',
    });
  }

  async createTag() {
    const modal = await this.modalCtrl.create({
      component: EditTagComponent,
      componentProps: {},
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getTags();
    });
  }

  async editTag(id: string) {
    this.navController.navigateForward(this.router.url, {
      queryParams: {
        id,
      },
    });
  }
}
